/**
 * SuggestedRulesPanel - Bank Transactions Center · iter 741
 * ===================================================
 *
 * Surfaces vendor patterns the user has classified the same way ≥3
 * times in the last 30 days but for which no rule yet exists. Each
 * card has a one-click "Create rule" button that posts to the existing
 * `/training-examples/from-transaction` endpoint using the sample tx.
 *
 * Auto-hides when there are no suggestions.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Brain, Sparkles, Loader2, X, Check, TrendingDown } from 'lucide-react';

const SuggestedRulesPanel = ({ onAfterAccept }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(new Set());
  const [dismissedIds, setDismissedIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(
          `${API}/ai-corrections/learning/rule-suggestions?min_occurrences=3&days=30`,
        );
        if (!cancelled) setSuggestions(resp.data?.suggestions || []);
      } catch (e) {
        // silent - panel just hides itself
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const accept = useCallback(async (s) => {
    const txId = s.sample_transaction_id;
    if (!txId) {
      return;
    }
    setBusyIds((prev) => new Set([...prev, txId]));
    try {
      const resp = await axios.post(
        `${API}/ai-corrections/training-examples/from-transaction`,
        {
          transaction_id: txId,
          amount_band_pct: 20.0,
          scope: 'bank_feed',
        },
      );
      if (resp.data?.success) {
        toast.success(
          resp.data.created
            ? `Rule created for ${s.counterparty}: saves ≈${s.estimated_credits_saved_per_month} credits/mo`
            : 'A matching rule already exists.',
        );
        setDismissedIds((prev) => new Set([...prev, txId]));
        onAfterAccept?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create rule');
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(txId);
        return next;
      });
    }
  }, [onAfterAccept]);

  const dismiss = useCallback((s) => {
    setDismissedIds((prev) => new Set([...prev, s.sample_transaction_id]));
  }, []);

  const visible = suggestions.filter((s) => !dismissedIds.has(s.sample_transaction_id));
  if (loading) return null;
  if (visible.length === 0) return null;

  return (
    <Card
      data-testid="suggested-rules-panel"
      className="border-amber-200 bg-gradient-to-br from-amber-50/70 via-white to-emerald-50/40 dark:from-amber-950/20 dark:via-slate-900 dark:to-emerald-950/20"
    >
      <CardContent className="p-5 space-y-4">
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Suggested rules: save AI credits with one click
            </h3>
          </div>
          <Badge variant="outline" className="text-[10px]">
            {visible.length} pattern{visible.length === 1 ? '' : 's'}
          </Badge>
        </header>

        <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
          You&apos;ve classified these the same way at least 3 times. Turn each into a deterministic rule and future identical transactions will skip AI entirely.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {visible.slice(0, 6).map((s) => {
            const busy = busyIds.has(s.sample_transaction_id);
            return (
              <div
                key={s.sample_transaction_id}
                data-testid={`suggested-rule-${s.sample_transaction_id}`}
                className="rounded-lg ring-1 ring-slate-200 dark:ring-slate-700 bg-white/70 dark:bg-slate-900/50 p-3 flex flex-col gap-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                      {s.counterparty}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate" title={s.sample_description}>
                      {s.sample_description}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => dismiss(s)}
                    data-testid={`dismiss-suggested-${s.sample_transaction_id}`}
                    className="h-6 w-6 p-0 -mt-1 -mr-1 text-slate-400 hover:text-slate-600"
                    title="Dismiss"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 text-[11px] text-slate-600 dark:text-slate-300">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                    {s.direction === 'inflow' ? '↘ inflow' : '↗ outflow'}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                    {s.count}× / 30d
                  </span>
                  {s.gl_code && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                      GL {s.gl_code}
                    </span>
                  )}
                  {s.estimated_credits_saved_per_month > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      <TrendingDown className="w-3 h-3" />
                      ≈{s.estimated_credits_saved_per_month}/mo saved
                    </span>
                  )}
                </div>
                <div className="flex justify-end mt-1">
                  <Button
                    size="sm"
                    onClick={() => accept(s)}
                    disabled={busy}
                    data-testid={`accept-suggested-${s.sample_transaction_id}`}
                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                  >
                    {busy ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Creating…
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5 mr-1" /> Create rule
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {visible.length > 6 && (
          <p className="text-[11px] text-center text-slate-500 pt-1">
            + {visible.length - 6} more: open Train AI to see all
          </p>
        )}

        <div className="text-[10px] text-slate-400 border-t border-slate-200 dark:border-slate-800 pt-2 flex items-center gap-1">
          <Brain className="w-3 h-3" /> AI is your safety net: rules cost zero credits per match.
        </div>
      </CardContent>
    </Card>
  );
};

export default SuggestedRulesPanel;
